import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import useSWR from 'swr';
import { KeyHint } from '@/components/KeyHint';
import { MultiSelect } from '@/components/MultiSelect';
import { Modal, ModalVariant } from '@/components/Modal';
import { TextField } from '@/components/TextField';
import { CACHE_KEYS } from '@/constants/cache-keys';
import { I18nNamespace } from '@/lib/i18n-namespaces';
import { EngineService } from '@/services/engine';
import { CreateVehiclePayload, Vehicle, UpdateVehiclePayload } from '@/types';
import { isSamePlate } from '../utils/duplicatePlate';

type AddVehicleModalMode = 'add' | 'recognize' | 'edit';

type AddVehicleModalProps = {
    isOpen: boolean;
    onClose: () => void;
    isSubmitting?: boolean;
    mode?: AddVehicleModalMode;
    onSubmit?: (payload: CreateVehiclePayload) => Promise<void>;
    onUpdate?: (
        vehicleId: string,
        payload: UpdateVehiclePayload,
    ) => Promise<boolean>;
    vehicle?: Vehicle | null;
};

export function AddVehicleModal({
    isOpen,
    onClose,
    isSubmitting = false,
    mode = 'add',
    onSubmit,
    onUpdate,
    vehicle,
}: AddVehicleModalProps) {
    const { t } = useTranslation([
        I18nNamespace.DIRECTORY,
        I18nNamespace.COMMON,
    ]);
    const [licensePlate, setLicensePlate] = useState('');
    const [alias, setAlias] = useState('');
    const [tags, setTags] = useState<string[]>([]);
    const { data: tagOptions = [] } = useSWR(
        isOpen ? CACHE_KEYS.ALL_VEHICLES_TAGS : null,
        () => EngineService.getVehicleTags(),
    );

    const multiSelectOptions = useMemo(
        () =>
            Array.from(new Set([...tagOptions, ...tags])).map((tag) => ({
                value: tag,
                label: tag,
            })),
        [tagOptions, tags],
    );

    useEffect(() => {
        if (!isOpen) {
            return;
        }
        if (mode === 'add') {
            setLicensePlate('');
            setAlias('');
            setTags([]);
            return;
        }
        if ((mode === 'recognize' || mode === 'edit') && vehicle) {
            setLicensePlate(vehicle.licensePlate);
            setAlias(vehicle.name || '');
            setTags(vehicle.tags || []);
        }
    }, [isOpen, mode, vehicle]);

    const title =
        mode === 'edit'
            ? t('vehicles.editModal.title')
            : mode === 'recognize'
              ? t('vehicles.recognizeModal.title')
              : t('vehicles.addModal.title');

    const submitLabel =
        mode === 'edit'
            ? t('vehicles.editModal.submit')
            : mode === 'recognize'
              ? t('vehicles.recognizeModal.submit')
              : t('vehicles.addModal.submit');

    const resetForm = () => {
        setLicensePlate('');
        setAlias('');
        setTags([]);
    };

    const handleSubmit = async () => {
        if (!licensePlate.trim()) {
            return;
        }

        const name = alias.trim() || undefined;

        if ((mode === 'recognize' || mode === 'edit') && vehicle && onUpdate) {
            const nextPlate = isSamePlate(licensePlate, vehicle.licensePlate)
                ? vehicle.licensePlate
                : licensePlate.trim();
            const updated = await onUpdate(vehicle.id, {
                licensePlate: nextPlate,
                name,
                tags,
            });
            if (!updated) {
                return;
            }
        } else if (onSubmit) {
            await onSubmit({
                licensePlate: licensePlate.trim(),
                name,
                tags,
            });
            resetForm();
        }
    };

    const handleClose = () => {
        resetForm();
        onClose();
    };

    const isSubmitDisabled = !licensePlate.trim() || isSubmitting;

    return (
        <Modal
            isOpen={isOpen}
            onClose={handleClose}
            title={title}
            onSubmit={() => void handleSubmit()}
            submitText={submitLabel}
            submitDisabled={isSubmitDisabled}
            submitInProgress={isSubmitting}
            variant={ModalVariant.PLAIN}
            size="small"
            submitButtonTrailingIcon={<KeyHint size="enter">↵</KeyHint>}
        >
            <div className="flex flex-col gap-6 px-6 pb-8">
                <TextField
                    label={t('vehicles.addModal.vehicleName')}
                    labelClassName="text-sm-medium text-white-100"
                    value={alias}
                    onChange={(event) => setAlias(event.target.value)}
                    placeholder={t('vehicles.addModal.vehicleNamePlaceholder')}
                    autoFocus
                />
                <TextField
                    label={t('vehicles.addModal.licensePlate')}
                    labelClassName="text-sm-medium text-white-100"
                    required
                    value={licensePlate}
                    onChange={(event) => setLicensePlate(event.target.value)}
                    placeholder={t('vehicles.addModal.licensePlatePlaceholder')}
                    disabled={mode === 'recognize'}
                />
                <MultiSelect
                    label={t('common:browseBar.tags')}
                    value={tags}
                    options={multiSelectOptions}
                    onChange={setTags}
                    placeholder={t('vehicles.addModal.addTag')}
                    searchPlaceholder={t('vehicles.addModal.tagPlaceholder')}
                    emptyMessage={t('common:browseBar.noMatchingTags')}
                    allowCreate
                    createLabel={(value) =>
                        `${t('vehicles.addModal.addTag')} "${value}"`
                    }
                />
            </div>
        </Modal>
    );
}
